import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const audioInsights = [
  { icon: 'ri-speed-line', label: '语速变化', desc: '识别教师在不同教学环节的语速转换' },
  { icon: 'ri-pause-circle-line', label: '停顿位置', desc: '核心概念前后是否留出停顿' },
  { icon: 'ri-volume-up-line', label: '重音分布', desc: '关键结论和概念是否得到强调' },
  { icon: 'ri-music-line', label: '句段转换', desc: '案例叙述与理论分析间的节奏边界' },
];

const sceneLimits = [
  '讲授节奏是否与内容结构匹配',
  '重点概念和结论是否得到清楚强调',
  '不同教学环节之间是否形成可辨认的声音边界',
];

export default function P29AudioUnderstanding({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-[11px] font-body font-semibold">
              <i className="ri-scan-line text-xs" /> 技术点三
            </span>
          </div>
          <h2 className={`font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            课堂声音理解：<span className="text-primary-600">节奏、停顿与表达推进</span>
          </h2>
        </div>

        {/* 场景说明横幅 */}
        <div className={`max-w-4xl mx-auto mb-6 transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="p-4 rounded-2xl glass-liquid-gold border-accent-200/40 text-center">
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-2 mb-2">
                <i className="ri-alert-line text-accent-500" />
                <span className="text-xs font-body font-bold text-accent-700 uppercase tracking-wider">场景说明</span>
              </div>
              <p className="text-xs md:text-sm text-foreground-600 font-body leading-relaxed">
                本次大赛采用限时教学展示形式，现场没有真实学生参与，不具备观察学生回应、教师追问的条件。
                <strong className="text-accent-700">本页不使用参赛选手作为互动案例</strong>，也不依据互动广度、互动深度对参赛选手作个案判断。
              </p>
            </div>
          </div>
        </div>

        {/* 声音分析四个维度 */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto mb-6 transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {audioInsights.map((item, i) => (
            <div key={item.label} className="p-4 rounded-2xl glass border-white/50 text-center hover:border-primary-200/40 transition-all duration-500" style={{ transitionDelay: `${400 + i * 80}ms` }}>
              <div className="w-10 h-10 mx-auto mb-2 rounded-xl glass-blue border-primary-200/40 flex items-center justify-center">
                <i className={`${item.icon} text-base text-primary-600`} />
              </div>
              <p className="text-xs font-body font-bold text-foreground-800">{item.label}</p>
              <p className="text-[11px] text-foreground-500 font-body mt-1">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* 大赛场景三种判断 */}
        <div className={`max-w-3xl mx-auto mb-5 transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 rounded-2xl glass-strong border-primary-200/30">
            <h3 className="font-heading text-xs font-bold text-foreground-800 mb-3 flex items-center gap-2">
              <i className="ri-speak-line text-primary-500" />
              大赛场景中声音理解主要服务三个判断
            </h3>
            <div className="space-y-2">
              {sceneLimits.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full glass-blue border-primary-200/40 flex items-center justify-center flex-shrink-0">
                    <span className="font-mono text-[10px] font-bold text-primary-600">{i + 1}</span>
                  </span>
                  <span className="text-xs text-foreground-600 font-body">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 底部说明 */}
        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl glass-gold border-accent-200/30">
            <i className="ri-information-line text-sm text-accent-500" />
            <span className="text-xs font-body text-foreground-500">
              真正的互动评价应放在有真实学生参与的常态课堂中完成，系统需先识别课堂场景再决定评价边界
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}